/**
 * src/domain/social/channel-contracts.ts
 *
 * Core domain contracts for Channels, Posts, and Cryptographic Invites in the Sovereign Social Portal.
 * Implements:
 * - Consumer Social Channel Categories (Town Square, Creators & Media, Tech & DePIN, Local Hangouts, Private Circles)
 * - 3-tier Access Spectrum (Public, Community Read-Only, Invite-Only Private)
 * - Cryptographic signed invite tokens with view-only vs member posting permissions
 * - Guaranteed 100% organic reach chronological feeds
 */

export type ChannelCategory =
  | 'town-square'
  | 'creators-media'
  | 'tech-depin'
  | 'local-hangouts'
  | 'private-circles'
  | 'sponsor-vault';

export type ChannelAccessLevel =
  | 'public'               // Open discovery, anyone can view, Tier 1+ can post
  | 'community-read-only'  // Anyone can read, only channel owner posts, threaded replies
  | 'invite-only';         // Hidden from public discovery, requires signed cryptographic invite

export interface ChannelCapabilities {
  canView: boolean;
  canPost: boolean;
}

export interface ChannelInviteToken {
  token: string;
  channelId: string;
  capabilities: ChannelCapabilities;
  maxUses: number;           // 1 for single-use, >1 for multi-use, 0 for unlimited
  usedCount: number;
  expiresAt: number;         // Unix timestamp ms
  creatorPersonaId: string;
  creatorSignature: string;  // Ed25519 creator signature
  createdAt: number;
}

export interface SocialChannel {
  id: string;
  slug: string;              // e.g. 'kl-street-food' -> herx.org/c/kl-street-food
  title: string;
  description: string;
  category: ChannelCategory;
  accessLevel: ChannelAccessLevel;
  ownerPersonaId: string;
  ownerDisplayName: string;
  ownerHandle: string;
  subscriberCount: number;
  minSovereignScore?: number; // Minimum score required to post (e.g. 50 to prevent bots)
  organicReachGuarantee: boolean; // Immutable true in Sovereign Mesh
  adRevenueShare: {
    creatorPercent: number;  // 70
    networkPercent: number;  // 30
  };
  tags: string[];
  isCustomUserCreated?: boolean;
  isArchived?: boolean;
  isDiscoverable?: boolean;
  isMature?: boolean;
  visaRequired?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MediaAttachment {
  type: 'image' | 'video' | 'document';
  url: string;
  aspectRatio?: string;
  c2paAttested?: boolean;
}

export interface SocialPost {
  id: string;
  channelId: string;
  authorPersonaId: string;
  authorDisplayName: string;
  authorHandle: string;
  authorAvatarUrl: string;
  authorTrustScore: number;
  contentMarkdown: string;
  mediaAttachments: MediaAttachment[];
  endorsementScore: number;     // Sum of staked community endorsements
  reactions: Record<string, number>; // emoji -> count
  remeshCount: number;          // re-posts / shares
  canonicalHash: string;        // SHA-256 of content + timestamp for provenance
  subscriberReachCount: number; // exact count of subscribers who received delivery
  createdAt: string;
}

/**
 * Validates and sorts posts in strict chronological order,
 * ensuring anti-algorithmic 100% organic reach guarantee.
 */
export function sortPostsChronologically(posts: SocialPost[], order: 'newest' | 'oldest' = 'newest'): SocialPost[] {
  return [...posts].sort((a, b) => {
    const timeA = new Date(a.createdAt).getTime();
    const timeB = new Date(b.createdAt).getTime();
    return order === 'newest' ? timeB - timeA : timeA - timeB;
  });
}

/**
 * Filter posts by subscribed channels.
 */
export function filterFollowingFeed(posts: SocialPost[], subscribedChannelIds: Set<string>): SocialPost[] {
  return sortPostsChronologically(
    posts.filter((p) => subscribedChannelIds.has(p.channelId)),
    'newest'
  );
}

/**
 * Evaluates whether a user can post to a specific channel given their Sovereign Score
 * and channel capability token.
 */
export function canUserPostToChannel(
  channel: SocialChannel,
  userScore: number,
  userPersonaId: string,
  userInvite?: ChannelInviteToken
): { allowed: boolean; reason?: string } {
  // Owner can always post
  if (channel.ownerPersonaId === userPersonaId) {
    return { allowed: true };
  }

  // If invite-only, user must hold a valid invite with canPost: true
  if (channel.accessLevel === 'invite-only') {
    if (!userInvite || !userInvite.capabilities.canPost) {
      return { allowed: false, reason: 'This is a private circle. A member invite is required to post.' };
    }
  }

  // If community read-only (broadcast), only owner posts
  if (channel.accessLevel === 'community-read-only') {
    return { allowed: false, reason: 'This is a broadcast channel. Only the creator can broadcast.' };
  }

  // Minimum Sovereign Score threshold check
  if (channel.minSovereignScore && userScore < channel.minSovereignScore) {
    return {
      allowed: false,
      reason: `Channel requires a Sovereign Score of at least ${channel.minSovereignScore} to post. Current score: ${userScore}.`,
    };
  }

  return { allowed: true };
}
